import { RequirePermission } from "@components/common/require-permission";
import { Divider } from "@mui/material";
import { Stack } from "@mui-wrapper";
import { useProjectCloneHelper } from "@pages/project-profile/project-clone/use-project-clone-helper";
import { HelpIconTooltip } from "@components/common/help-icon-tooltip";
import { BaseProjectProps } from "@custom-types/base-types";
import { ActionButtonSx } from "@styles/common-styles";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { RichTextField } from "@components/common/rich-text-field";
import { Switch, Button, Dialog, Alert, Text } from "@flat-ui";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import { DropdownWithHelp } from "@components/common/dropdown-with-help";

/** A button opening a dialog to clone a project */
export function ProjectClone({ project }: BaseProjectProps): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const {
    cloneProjectPayload,
    authorizedMembers,
    groups,
    fetching,
    getCompanyName,
    getMemberName,
    onCloneProjectPayloadChange,
    onCloneProject,
    handleDialogClose,
    shouldShowDialog,
    setShouldShowDialog,
    shouldDisableConfirmButton,
    shouldAddToCurrentCompany,
    setShouldAddToCurrentCompany,
    shouldAddToAuthorizedMember,
    setShouldAddToAuthorizedMember,
    shouldAddProjectMembers,
    setShouldAddProjectMembers,
  } = useProjectCloneHelper({ project });

  return (
    <>
      <RequirePermission permission="project.clone">
        <Button
          sx={{ ...ActionButtonSx }}
          onClick={() => {
            trackEvent({
              name: ProjectEvents.openCloneProjectDialog,
            });
            setShouldShowDialog(true);
          }}
          variant="secondary"
        >
          Clone Project
        </Button>
      </RequirePermission>

      <Dialog
        open={shouldShowDialog}
        title="Cloning Project"
        onCancel={handleDialogClose}
        cancelText="Close"
        onConfirm={onCloneProject}
        isConfirmDisabled={shouldDisableConfirmButton()}
        showSpinner={fetching.isCloningProject}
      >
        <Text variant="bodyL" sx={{ mb: 2 }}>
          Please provide the following information for the cloned project:
        </Text>
        <Alert
          variant="warning"
          title="Face blurring is not carried over when cloning a project. If it was
            activated on the original project, remember to activate it manually
            on the cloned project."
        />
        {/* Project name */}
        <RichTextField
          label="Project Name for the new cloned project"
          text={cloneProjectPayload.projectName}
          onChange={(text) => onCloneProjectPayloadChange("projectName", text)}
        />
        {/* Company ID switch and input */}
        <Switch
          label="Should the new cloned project be added to the current workspace?"
          checked={shouldAddToCurrentCompany}
          onToggle={() =>
            setShouldAddToCurrentCompany(!shouldAddToCurrentCompany)
          }
          sx={{ mt: 2, ml: 0 }}
        />
        {!shouldAddToCurrentCompany && (
          <RichTextField
            label="Workspace ID for the new cloned project"
            text={cloneProjectPayload.targetCompanyId}
            onChange={(text) =>
              onCloneProjectPayloadChange("targetCompanyId", text)
            }
            helpText={getCompanyName()}
            isLoading={fetching.isFetchingCompany}
          />
        )}

        {/* Target group selection */}

        <DropdownWithHelp
          value={cloneProjectPayload.targetGroupId}
          onChange={(event) =>
            onCloneProjectPayloadChange("targetGroupId", event.target.value)
          }
          fullWidth
          label="Target Group"
          disabled={fetching.isFetchingGroups || groups.length === 0}
          options={groups.map(({ id, name }) => ({
            key: id,
            value: id,
            label: <TextWithTooltip text={name} />,
            secondaryText: `id: ${id}`,
          }))}
          helpTooltip="The new cloned project will be added to this group."
        />

        {/* Authorized memberId selection */}
        <DropdownWithHelp
          value={cloneProjectPayload.authorizedBy}
          onChange={(event) =>
            onCloneProjectPayloadChange("authorizedBy", event.target.value)
          }
          fullWidth
          label="Authorized Member"
          disabled={
            fetching.isFetchingGroupMembers ||
            fetching.isFetchingCompanyMembers ||
            authorizedMembers.length === 0
          }
          options={authorizedMembers.map(({ identity, email }) => ({
            key: identity,
            value: identity,
            label: <TextWithTooltip text={email} />,
            secondaryText: `id: ${identity}`,
          }))}
          helpTooltip="This is the person who allows the project to be copied into the target workspace. It might not be the same person who asked to copy the project. The authorized member must be a group manager or an enterprise admin of the target group, because they need permission to create groups in the workspace. This information is only saved for record-keeping. It doesn't affect who can manage the project later."
        />

        {/* project manager ID switch and input */}
        <Switch
          label="Is the authorizing member same as the project manager for the cloned project?"
          checked={shouldAddToAuthorizedMember}
          onToggle={() =>
            setShouldAddToAuthorizedMember(!shouldAddToAuthorizedMember)
          }
          sx={{ mt: 2, ml: 0 }}
        />
        {!shouldAddToAuthorizedMember && (
          <RichTextField
            label="Project Manager ID for the new cloned project"
            text={cloneProjectPayload.projectManager}
            onChange={(text) =>
              onCloneProjectPayloadChange("projectManager", text)
            }
            helpText={getMemberName()}
            isLoading={fetching.isFetchingMember}
            helpTooltip={
              <>
                This member will be assigned as the project manager for the
                cloned project. Key details:
                <ul>
                  <li>
                    Typically the same as the authorized member (who already has
                    necessary permissions)
                  </li>
                  <li>Can be any member, even from a different workspace</li>
                  <li>
                    Can be changed later by any group manager or enterprise
                    admin
                  </li>
                  <li>
                    If not part of the target group/workspace, they'll be
                    automatically invited (counts as a new active member)
                  </li>
                  <li>
                    If the project manager of the cloned project is different
                    than the original project, then the old project manager will
                    become an admin in the newly cloned project.
                  </li>
                </ul>
              </>
            }
          />
        )}
        <Divider sx={{ mb: 2 }} />
        <b>Additional Options:</b>
        <Stack
          sx={{
            flexDirection: "row",
            mt: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Switch
            label="Should copy project members?"
            checked={shouldAddProjectMembers}
            onToggle={() =>
              setShouldAddProjectMembers(!shouldAddProjectMembers)
            }
          />
          <HelpIconTooltip
            title={
              <>
                Turn this on to copy all permissions of members from the
                original project to the cloned project
                <ul>
                  <li>
                    Only direct project members will be copied over, for users
                    which are not in the target workspace yet - will
                    automatically be invited
                  </li>
                  <li>Teams are NOT considered at all or copied over</li>
                  <li>
                    Group permissions like Group Viewers or Group Managers are
                    also NOT considered
                  </li>
                </ul>
              </>
            }
          />
        </Stack>
      </Dialog>
    </>
  );
}
